import { Modal } from "@/components/base/Modal";
import { variantLabel } from "@/lib/format";
import {
  assertRequestedQtyAgainstAvailable,
  availableForExactVariantLocation,
  fulfilmentFromOptionLabel,
  recommendFulfilmentLocation,
  resolveSelectedFulfilmentLocation,
  type LocationStockOption,
} from "@/lib/fulfilment-location";
import { remainingFulfilmentQty } from "@/lib/sales-orders";

export type FulfilModalItem = {
  id: string;
  product_id: string;
  variant_id: string | null;
  qty_ordered: number;
  qty_fulfilled: number;
  qty_cancelled?: number | null;
  is_bundle?: boolean | null;
  products?: { name: string; sku?: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
};

export function FulfilOrderModal(props: {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  orderLocationId: string | null | undefined;
  items: FulfilModalItem[];
  qtyByLineId: Record<string, number>;
  locByLineId: Record<string, string | undefined>;
  onQtyChange: (lineId: string, qty: number) => void;
  onLocChange: (lineId: string, locationId: string) => void;
  locations: LocationStockOption[];
  balances: Record<string, { available: number }>;
  error: string;
  saving: boolean;
  confirmDisabled: boolean;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={`Fulfil — ${props.orderNumber}`}
      footer={
        <>
          <button type="button" onClick={props.onClose} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Cancel
          </button>
          <button type="button" onClick={props.onConfirm} disabled={props.confirmDisabled} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            {props.saving ? "Fulfilling..." : "Confirm Fulfilment"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {props.error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{props.error}</div>}
        <p className="text-xs text-slate-500">From is the warehouse that will be validated and sent to fulfilment. Duplicate names are distinguished by available stock — pick the location with stock, not an empty copy. Imported orders may have no order location; choose From here.</p>
        {props.items.filter((it) => !it.is_bundle).map((it) => {
          const remaining = remainingFulfilmentQty(it.qty_ordered, it.qty_cancelled ?? 0, it.qty_fulfilled);
          const selectedLoc = resolveSelectedFulfilmentLocation({
            selectedLocationId: props.locByLineId[it.id],
            orderLocationId: props.orderLocationId,
          }) ?? "";
          const locOptions = props.locations
            .map((l) => ({
              ...l,
              available: availableForExactVariantLocation(props.balances, it.product_id, it.variant_id, l.id),
            }))
            .sort((a, b) => b.available - a.available);
          const recommendedId = recommendFulfilmentLocation({
            locations: props.locations,
            availableByLocationId: Object.fromEntries(locOptions.map((l) => [l.id, l.available])),
            qtyNeeded: remaining,
            preferredLocationId: props.orderLocationId,
          }).locationId;
          const selectedAvailable = selectedLoc
            ? availableForExactVariantLocation(props.balances, it.product_id, it.variant_id, selectedLoc)
            : 0;
          const qty = props.qtyByLineId[it.id] ?? 0;
          const lineStockOk = qty <= 0 || (selectedLoc !== "" && assertRequestedQtyAgainstAvailable({
            requestedQty: qty,
            available: selectedAvailable,
          }).ok);
          return (
            <div key={it.id} className="rounded-lg border border-slate-100 p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {it.products?.name ?? "—"}
                    {it.variants && (variantLabel(it.variants.option_values) || it.variants.sku) && (
                      <span className="text-slate-500 font-normal ml-1">· {variantLabel(it.variants.option_values) || it.variants.sku}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400">
                    {it.qty_fulfilled} of {it.qty_ordered} fulfilled · {remaining} remaining
                  </p>
                </div>
                <input
                  type="number"
                  min="0"
                  max={remaining}
                  disabled={remaining <= 0}
                  value={qty}
                  onChange={(e) => props.onQtyChange(it.id, Number(e.target.value))}
                  className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50"
                  aria-label={`Quantity for ${it.products?.name ?? it.id}`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 shrink-0" htmlFor={`fulfil-from-${it.id}`}>From</label>
                <select
                  id={`fulfil-from-${it.id}`}
                  value={selectedLoc}
                  onChange={(e) => props.onLocChange(it.id, e.target.value)}
                  className="flex-1 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                  aria-label={`From location for ${it.products?.name ?? it.id}`}
                >
                  {!selectedLoc && <option value="">Select a location</option>}
                  {locOptions.map((l) => (
                    <option key={l.id} value={l.id}>
                      {fulfilmentFromOptionLabel(l.name, l.available, l.id === recommendedId)}
                    </option>
                  ))}
                  {locOptions.length === 0 && <option value="">No locations</option>}
                </select>
                <span className={`text-xs whitespace-nowrap ${selectedAvailable > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                  {selectedAvailable} available
                </span>
              </div>
              {!lineStockOk && (
                <p className="text-xs text-amber-700 mt-2">Requested quantity exceeds available stock at the selected location.</p>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
